import { supabase } from "@/integrations/supabase/client";
import axios from "axios";

interface ClimateData {
  averageTemperature: number;
  averagePrecipitation: number;
  averageHumidity: number;
  averageWindSpeed: number;
  climateZone: string;
  historicalTrends: {
    temperature: (number | null)[];
    precipitation: (number | null)[];
    humidity: (number | null)[];
    windSpeed: (number | null)[];
    solarRadiation: (number | null)[];
    soilMoisture: (number | null)[];
    years: string[];
  };
  indicators: {
    hotDays: number;
    frostDays: number;
    wetDays: number;
    maxTemperature: number;
    minTemperature: number;
    temperatureRange: number;
    averageSolarRadiation: number;
    averageSoilMoisture: number;
  };
}

interface SatelliteImage {
  url: string;
  timestamp: string;
  source: string;
  resolution: string;
}

interface PolicyData {
  name: string;
  description: string;
  implementationDate: string;
  status: string;
  impact: string;
}

interface ConservationProject {
  name: string;
  description: string;
  startDate: string;
  status: string;
  impact: string;
  organization: string;
}

interface EnrichedRegionData {
  climate: ClimateData;
  satelliteImages: SatelliteImage[];
  policies: PolicyData[];
  conservationProjects: ConservationProject[];
}

// NASA POWER API base URL for climate data
const CLIMATE_API_BASE =
  "https://power.larc.nasa.gov/api/temporal/daily/point?";

export const fetchEnrichedData = async (
  regionId: string,
): Promise<EnrichedRegionData> => {
  try {
    // Fetch climate data
    const climateData = await fetchClimateData(regionId);

    // Fetch satellite images
    const satelliteImages = await fetchSatelliteImages(regionId);

    // Fetch policy data
    let policies = await fetchPolicyData(regionId);

    // Fetch conservation projects
    let conservationProjects = await fetchConservationProjects(regionId);

    // If no policies or projects are found, return default data
    if (!policies || policies.length === 0) {
      policies = [
        {
          name: "No Policies Found",
          description: "There are no recorded policies for this region yet.",
          implementationDate: new Date().toISOString().split("T")[0],
          status: "Pending",
          impact: "No impact data available",
        },
      ];
    }

    if (!conservationProjects || conservationProjects.length === 0) {
      conservationProjects = [
        {
          name: "No Projects Found",
          description:
            "There are no recorded conservation projects for this region yet.",
          startDate: new Date().toISOString().split("T")[0],
          status: "Planned",
          impact: "No impact data available",
          organization: "No organization data available",
        },
      ];
    }

    return {
      climate: climateData,
      satelliteImages,
      policies,
      conservationProjects,
    };
  } catch (error) {
    console.error("Error fetching enriched data:", error);
    // Return default data structure even if there's an error
    return {
      climate: {
        averageTemperature: 0,
        averagePrecipitation: 0,
        averageHumidity: 0,
        averageWindSpeed: 0,
        climateZone: "Unknown",
        historicalTrends: {
          temperature: [],
          precipitation: [],
          humidity: [],
          windSpeed: [],
          solarRadiation: [],
          soilMoisture: [],
          years: [],
        },
        indicators: {
          hotDays: 0,
          frostDays: 0,
          wetDays: 0,
          maxTemperature: 0,
          minTemperature: 0,
          temperatureRange: 0,
          averageSolarRadiation: 0,
          averageSoilMoisture: 0,
        },
      },
      satelliteImages: [],
      policies: [
        {
          name: "No Data Available",
          description: "Unable to fetch policy data at this time.",
          implementationDate: new Date().toISOString().split("T")[0],
          status: "Pending",
          impact: "No impact data available",
        },
      ],
      conservationProjects: [
        {
          name: "No Data Available",
          description:
            "Unable to fetch conservation project data at this time.",
          startDate: new Date().toISOString().split("T")[0],
          status: "Planned",
          impact: "No impact data available",
          organization: "No organization data available",
        },
      ],
    };
  }
};

const fetchClimateData = async (regionId: string): Promise<ClimateData> => {
  try {
    const { data: region, error } = await supabase
      .from("regions")
      .select("coordinates, name")
      .eq("id", regionId)
      .single();

    if (error || !region) {
      throw new Error("Failed to fetch region coordinates");
    }

    const [longitude, latitude] = region.coordinates;
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 5;

    // Use NASA POWER API for climate data with expanded parameters
    const response = await axios.get(CLIMATE_API_BASE, {
      params: {
        start: `${startYear}0101`,
        end: `${currentYear}1231`,
        latitude: latitude.toFixed(4),
        longitude: longitude.toFixed(4),
        community: "RE",
        parameters:
          "T2M,T2M_MAX,T2M_MIN,T2M_RANGE,PRECTOTCORR,RH2M,WS2M,ALLSKY_SFC_SW_DWN,GWETPROF,QV2M",
        format: "JSON",
        header: "true",
        "time-standard": "UTC",
      },
    });

    const parameter = response.data?.properties?.parameter;
    if (!parameter || typeof parameter !== "object") {
      console.error("Invalid climate data response:", response.data);
      throw new Error("Invalid climate data response structure");
    }

    // Validate required parameters exist
    const requiredParams = [
      "T2M",
      "T2M_MAX",
      "T2M_MIN",
      "T2M_RANGE",
      "PRECTOTCORR",
      "RH2M",
      "WS2M",
      "ALLSKY_SFC_SW_DWN",
      "GWETPROF",
      "QV2M",
    ];
    const missingParams = requiredParams.filter(
      (param) => !(param in parameter),
    );
    if (missingParams.length > 0) {
      console.error("Missing climate parameters:", missingParams);
      throw new Error(
        `Missing required climate parameters: ${missingParams.join(", ")}`,
      );
    }

    const climateData = parameter as {
      T2M: Record<string, number>;
      T2M_MAX: Record<string, number>;
      T2M_MIN: Record<string, number>;
      T2M_RANGE: Record<string, number>;
      PRECTOTCORR: Record<string, number>;
      RH2M: Record<string, number>;
      WS2M: Record<string, number>;
      ALLSKY_SFC_SW_DWN: Record<string, number>;
      GWETPROF: Record<string, number>;
      QV2M: Record<string, number>;
    };

    console.log("Raw T2M data:", climateData.T2M);

    // Process and aggregate yearly data
    const years = Object.keys(climateData.T2M || {})
      .map((date) => date.substring(0, 4))
      .filter((year, index, self) => self.indexOf(year) === index)
      .sort();

    // Constants for validation
    const ABSOLUTE_MIN_TEMP = -100; // Slightly below lowest recorded temp (-89.2°C)
    const ABSOLUTE_MAX_TEMP = 60; // Slightly above highest recorded temp (56.7°C)
    const MAX_DAILY_PRECIP = 1000; // Maximum reasonable daily precipitation in mm
    const MAX_HUMIDITY = 100; // Maximum possible humidity percentage
    const MIN_HUMIDITY = 0; // Minimum possible humidity percentage
    const MAX_WIND_SPEED = 150; // Maximum reasonable 2m wind speed in m/s
    const MAX_SOLAR_RAD = 1370; // Maximum solar constant at Earth's surface (W/m²)
    const MAX_SOIL_MOISTURE = 1; // Maximum soil moisture as fraction (1 = 100%)

    const isValidTemperature = (temp: number): boolean => {
      return (
        typeof temp === "number" &&
        !isNaN(temp) &&
        temp > ABSOLUTE_MIN_TEMP &&
        temp < ABSOLUTE_MAX_TEMP
      );
    };

    const isValidPrecipitation = (prec: number): boolean => {
      return (
        typeof prec === "number" &&
        !isNaN(prec) &&
        prec >= 0 &&
        prec <= MAX_DAILY_PRECIP
      );
    };

    const isValidHumidity = (hum: number): boolean => {
      return (
        typeof hum === "number" &&
        !isNaN(hum) &&
        hum >= MIN_HUMIDITY &&
        hum <= MAX_HUMIDITY
      );
    };

    const isValidWindSpeed = (wind: number): boolean => {
      return (
        typeof wind === "number" &&
        !isNaN(wind) &&
        wind >= 0 &&
        wind <= MAX_WIND_SPEED
      );
    };

    const isValidSolarRadiation = (rad: number): boolean => {
      return (
        typeof rad === "number" &&
        !isNaN(rad) &&
        rad >= 0 &&
        rad <= MAX_SOLAR_RAD
      );
    };

    const isValidSoilMoisture = (moisture: number): boolean => {
      return (
        typeof moisture === "number" &&
        !isNaN(moisture) &&
        moisture >= 0 &&
        moisture <= MAX_SOIL_MOISTURE
      );
    };

    const temperatures: (number | null)[] = [];
    const precipitations: (number | null)[] = [];
    const humidityValues: (number | null)[] = [];
    const windSpeeds: (number | null)[] = [];
    const solarRadiation: (number | null)[] = [];
    const soilMoisture: (number | null)[] = [];

    // Track overall temperature extremes
    let globalMaxTemp = -Infinity;
    let globalMinTemp = Infinity;

    years.forEach((year) => {
      let tempSum = 0,
        precSum = 0,
        humSum = 0,
        windSum = 0,
        solarSum = 0,
        soilSum = 0,
        maxTemp = -Infinity,
        minTemp = Infinity,
        tempCount = 0,
        precCount = 0,
        humCount = 0,
        windCount = 0,
        solarCount = 0,
        soilCount = 0;

      // Process each parameter for the year
      if (climateData.T2M) {
        Object.entries(climateData.T2M).forEach(([date, value]) => {
          if (date.startsWith(year) && isValidTemperature(value)) {
            tempSum += value;
            tempCount++;
            maxTemp = Math.max(maxTemp, value);
            minTemp = Math.min(minTemp, value);
            // Update global extremes
            globalMaxTemp = Math.max(globalMaxTemp, value);
            globalMinTemp = Math.min(globalMinTemp, value);
          }
        });
      }

      // Process precipitation data
      if (climateData.PRECTOTCORR) {
        Object.entries(climateData.PRECTOTCORR).forEach(([date, value]) => {
          if (date.startsWith(year) && isValidPrecipitation(value)) {
            precSum += value;
            precCount++;
          }
        });
      }

      // Process humidity data
      if (climateData.RH2M) {
        Object.entries(climateData.RH2M).forEach(([date, value]) => {
          if (date.startsWith(year) && isValidHumidity(value)) {
            humSum += value;
            humCount++;
          }
        });
      }

      // Process wind speed data
      if (climateData.WS2M) {
        Object.entries(climateData.WS2M).forEach(([date, value]) => {
          if (date.startsWith(year) && isValidWindSpeed(value)) {
            windSum += value;
            windCount++;
          }
        });
      }

      // Process solar radiation data
      if (climateData.ALLSKY_SFC_SW_DWN) {
        Object.entries(climateData.ALLSKY_SFC_SW_DWN).forEach(
          ([date, value]) => {
            if (date.startsWith(year) && isValidSolarRadiation(value)) {
              solarSum += value;
              solarCount++;
            }
          },
        );
      }

      // Process soil moisture data
      if (climateData.GWETPROF) {
        Object.entries(climateData.GWETPROF).forEach(([date, value]) => {
          if (date.startsWith(year) && isValidSoilMoisture(value)) {
            soilSum += value;
            soilCount++;
          }
        });
      }

      // Push yearly totals/averages: temperature is mean (°C), precipitation is annual total (mm/year)
      // Always push (null if no valid data) to keep all arrays index-aligned with years[]
      temperatures.push(
        tempCount > 0 ? parseFloat((tempSum / tempCount).toFixed(1)) : null,
      );
      precipitations.push(
        precCount > 0 ? parseFloat(precSum.toFixed(1)) : null,
      ); // annual total mm/year
      humidityValues.push(
        humCount > 0 ? parseFloat((humSum / humCount).toFixed(1)) : null,
      );
      windSpeeds.push(
        windCount > 0 ? parseFloat((windSum / windCount).toFixed(1)) : null,
      );
      solarRadiation.push(
        solarCount > 0 ? parseFloat((solarSum / solarCount).toFixed(1)) : null,
      );
      soilMoisture.push(
        soilCount > 0 ? parseFloat((soilSum / soilCount).toFixed(3)) : null,
      );
    });

    // Climatological means across all valid years (WMO standard: multi-year averages, not single-year)
    const mean = (arr: (number | null)[], decimals = 1): number => {
      const valid = arr.filter((v): v is number => v !== null);
      return valid.length > 0
        ? parseFloat(
            (valid.reduce((a, b) => a + b, 0) / valid.length).toFixed(decimals),
          )
        : 0;
    };
    const avgTemp = mean(temperatures);
    const avgPrec = mean(precipitations);
    const avgHum = mean(humidityValues);
    const avgWind = mean(windSpeeds);
    const avgSolar = mean(solarRadiation);
    const avgSoil = mean(soilMoisture, 3);

    // Calculate climate indicators with validation
    // Annual average hot days (T2M mean > 35°C): total across record divided by years
    const hotDays =
      climateData.T2M && years.length > 0
        ? Math.round(
            Object.values(climateData.T2M).filter(
              (temp) => isValidTemperature(temp) && temp > 35,
            ).length / years.length,
          )
        : 0;

    // Frost days: WMO definition = daily minimum temperature (T2M_MIN) < 0°C
    // Using T2M_MIN is more accurate than T2M mean, which significantly undercounts frost days
    // Report as annual average across all years in the record
    const frostSource = climateData.T2M_MIN ?? climateData.T2M;
    const totalFrostDays = frostSource
      ? Object.entries(frostSource)
          .filter(([_, temp]) => isValidTemperature(temp) && temp < 0)
          .map(([date]) => date.substring(0, 10))
          .filter((value, index, self) => self.indexOf(value) === index).length
      : 0;
    const frostDays =
      years.length > 0 ? Math.round(totalFrostDays / years.length) : 0;

    // FIXED: Calculate average wet days per year
    let yearlyWetDays: number[] = [];
    if (climateData.PRECTOTCORR) {
      years.forEach((year) => {
        const wetDaysThisYear = Object.entries(climateData.PRECTOTCORR).filter(
          ([date, prec]) =>
            date.startsWith(year) && isValidPrecipitation(prec) && prec > 1,
        ).length;
        yearlyWetDays.push(wetDaysThisYear);
      });
    }
    const wetDays =
      yearlyWetDays.length > 0
        ? Math.round(
            yearlyWetDays.reduce((a, b) => a + b, 0) / yearlyWetDays.length,
          )
        : 0;

    // Get temperature extremes from both T2M_MAX/MIN and regular T2M readings
    let maxFromAPI = -Infinity;
    let minFromAPI = Infinity;

    if (climateData.T2M_MAX) {
      const validMaxTemps = Object.values(climateData.T2M_MAX).filter((temp) =>
        isValidTemperature(temp),
      );
      if (validMaxTemps.length > 0) {
        maxFromAPI = Math.max(...validMaxTemps);
      }
    }

    if (climateData.T2M_MIN) {
      const validMinTemps = Object.values(climateData.T2M_MIN).filter((temp) =>
        isValidTemperature(temp),
      );
      if (validMinTemps.length > 0) {
        minFromAPI = Math.min(...validMinTemps);
      }
    }

    // Use the most extreme valid values from both sources and ensure they're within bounds
    const maxTemperature = Math.min(
      ABSOLUTE_MAX_TEMP,
      Math.max(globalMaxTemp, maxFromAPI),
    );
    const minTemperature = Math.max(
      ABSOLUTE_MIN_TEMP,
      Math.min(globalMinTemp, minFromAPI),
    );
    const temperatureRange = maxTemperature - minTemperature;

    // Validate final averages before using them
    const climateZone = determineDetailedClimateZone(
      isValidTemperature(avgTemp) ? avgTemp : 0,
      isValidPrecipitation(avgPrec) ? avgPrec : 0,
      hotDays,
      frostDays,
    );

    return {
      averageTemperature: isValidTemperature(avgTemp) ? avgTemp : 0,
      averagePrecipitation: isValidPrecipitation(avgPrec) ? avgPrec : 0,
      averageHumidity: isValidHumidity(avgHum) ? avgHum : 0,
      averageWindSpeed: isValidWindSpeed(avgWind) ? avgWind : 0,
      climateZone,
      historicalTrends: {
        temperature: temperatures,
        precipitation: precipitations,
        humidity: humidityValues,
        windSpeed: windSpeeds,
        solarRadiation: solarRadiation,
        soilMoisture: soilMoisture,
        years,
      },
      indicators: {
        hotDays,
        frostDays,
        wetDays,
        maxTemperature,
        minTemperature,
        temperatureRange,
        averageSolarRadiation: isValidSolarRadiation(avgSolar) ? avgSolar : 0,
        averageSoilMoisture: isValidSoilMoisture(avgSoil) ? avgSoil : 0,
      },
    };
  } catch (error) {
    console.error("Error fetching climate data:", error);
    return {
      averageTemperature: 0,
      averagePrecipitation: 0,
      averageHumidity: 0,
      averageWindSpeed: 0,
      climateZone: "Unknown",
      historicalTrends: {
        temperature: [],
        precipitation: [],
        humidity: [],
        windSpeed: [],
        solarRadiation: [],
        soilMoisture: [],
        years: [],
      },
      indicators: {
        hotDays: 0,
        frostDays: 0,
        wetDays: 0,
        maxTemperature: 0,
        minTemperature: 0,
        temperatureRange: 0,
        averageSolarRadiation: 0,
        averageSoilMoisture: 0,
      },
    };
  }
};

// Enhanced climate zone determination using multiple indicators
const determineDetailedClimateZone = (
  avgTemp: number,
  annualPrecip: number,
  hotDays: number,
  frostDays: number,
): string => {
  // Hot desert climate (BWh)
  if (annualPrecip < 250 && avgTemp > 18 && hotDays > 120) {
    return "Hot Desert";
  }

  // Tropical rainforest climate (Af)
  if (avgTemp > 18 && annualPrecip > 2000 && frostDays === 0) {
    return "Tropical Rainforest";
  }

  // Tropical monsoon climate (Am)
  if (avgTemp > 18 && annualPrecip > 1500 && frostDays === 0) {
    return "Tropical Monsoon";
  }

  // Tropical savanna climate (Aw)
  if (avgTemp > 18 && annualPrecip > 750 && frostDays === 0) {
    return "Tropical Savanna";
  }

  // Mediterranean climate (Csa/Csb)
  if (avgTemp > 10 && avgTemp < 18 && annualPrecip > 500) {
    return "Mediterranean";
  }

  // Humid subtropical climate (Cfa)
  if (avgTemp > 10 && annualPrecip > 1000 && frostDays < 90) {
    return "Subtropical Humid";
  }

  // Oceanic climate (Cfb)
  if (avgTemp > 0 && avgTemp < 18 && annualPrecip > 750 && frostDays < 120) {
    return "Oceanic";
  }

  // Continental climate (Dfa/Dfb)
  if (avgTemp > 0 && frostDays > 90) {
    return "Continental";
  }

  // Subarctic climate (Dfc)
  if (avgTemp < 10 && frostDays > 150) {
    return "Subarctic";
  }

  // Polar climate (ET/EF)
  if (avgTemp < 0) {
    return "Polar";
  }

  return "Temperate"; // Default fallback
};

const fetchSatelliteImages = async (
  regionId: string,
): Promise<SatelliteImage[]> => {
  try {
    // Fetch the region coordinates from the database
    const { data: region, error } = await supabase
      .from("regions")
      .select("coordinates")
      .eq("id", regionId)
      .single();

    if (error || !region) {
      throw new Error("Failed to fetch region coordinates");
    }

    const [longitude, latitude] = region.coordinates;
    const mapboxToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

    // Generate satellite images at different zoom levels
    const zoomLevels = [12, 14, 16]; // City, Neighborhood, and Building level views
    const images = zoomLevels.map((zoom) => ({
      url: `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/static/${longitude},${latitude},${zoom},0/${800}x${600}?access_token=${mapboxToken}`,
      timestamp: new Date().toISOString(),
      source: "Mapbox Satellite",
      resolution: `Zoom level ${zoom}`,
    }));

    return images;
  } catch (error) {
    console.error("Error fetching satellite images:", error);
    return [];
  }
};

const fetchPolicyData = async (regionId: string): Promise<PolicyData[]> => {
  try {
    const { data, error } = await supabase
      .from("policies")
      .select("*")
      .eq("region_id", regionId)
      .order("implementation_date", { ascending: false });

    if (error) {
      console.error("Error fetching policy data:", error);
      throw error;
    }

    return data.map((policy) => ({
      name: policy.name,
      description: policy.description,
      implementationDate: policy.implementation_date,
      status: policy.status,
      impact: policy.impact,
    }));
  } catch (error) {
    console.error("Error fetching policy data:", error);
    throw error;
  }
};

const fetchConservationProjects = async (
  regionId: string,
): Promise<ConservationProject[]> => {
  try {
    const { data, error } = await supabase
      .from("conservation_projects")
      .select("*")
      .eq("region_id", regionId)
      .order("start_date", { ascending: false });

    if (error) {
      console.error("Error fetching conservation projects:", error);
      throw error;
    }

    return data.map((project) => ({
      name: project.name,
      description: project.description,
      startDate: project.start_date,
      status: project.status,
      impact: project.impact,
      organization: project.organization,
    }));
  } catch (error) {
    console.error("Error fetching conservation projects:", error);
    throw error;
  }
};
